import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Col, Drawer, Form, Input, InputNumber, Row, Select, Spin } from "antd";
import { serverFetch } from "../../../core/server";
import { REPORT_FORM_TIP, REPORT_FORM_TIP_OPTIONS } from "./report-form-tip";
import "./spr-report-form.css";

const createEmptyReportForm = () => ({
  idForm: 0,
  idx: null,
  formName: "",
  formTip: REPORT_FORM_TIP.ORDERS,
  formBody: "{}",
  komment: "",
  userLevel: null,
  ver: null,
  guid: "",
  isGroup: false,
  forProfOnly: false
});

const normalizeReportForm = (item) => ({
  ...createEmptyReportForm(),
  ...item,
  formTip: Number(item?.formTip ?? item?.FormTip ?? REPORT_FORM_TIP.ORDERS),
  isGroup: !!(item?.isGroup ?? item?.IsGroup),
  forProfOnly: !!(item?.forProfOnly ?? item?.ForProfOnly)
});

const SprReportForm = ({ open, idForm, canEdit = true, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currentItem, setCurrentItem] = useState(null);
  const isNew = !idForm;

  const title = useMemo(() => (isNew ? "Новый шаблон отчета" : "Шаблон отчета"), [isNew]);

  const loadReportForm = useCallback(async () => {
    if (!open) return;

    setError("");
    if (isNew) {
      const empty = createEmptyReportForm();
      setCurrentItem(empty);
      form.setFieldsValue(empty);
      return;
    }

    setLoading(true);
    try {
      const item = await serverFetch(`app/database/reports/${idForm}`, { method: "GET" });
      const next = normalizeReportForm(item);
      setCurrentItem(next);
      form.setFieldsValue(next);
    } catch (e) {
      setError(e.message || "Не удалось загрузить шаблон отчета");
    } finally {
      setLoading(false);
    }
  }, [form, idForm, isNew, open]);

  useEffect(() => {
    loadReportForm();
  }, [loadReportForm]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const values = await form.validateFields();
      const body = {
        ...createEmptyReportForm(),
        ...(currentItem || {}),
        ...values,
        idForm: isNew ? 0 : Number(values?.idForm ?? idForm) || 0,
        formTip: Number(values.formTip),
        isGroup: !!values.isGroup,
        forProfOnly: !!(currentItem?.forProfOnly ?? currentItem?.ForProfOnly)
      };
      const saved = await serverFetch("app/database/reports", {
        method: isNew ? "POST" : "PUT",
        bodyData: body
      });
      onSaved?.(saved);
    } catch (e) {
      setError(e.message || "Не удалось сохранить шаблон отчета");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      placement="right"
      title={title}
      width={860}
      open={open}
      onClose={onClose}
      destroyOnClose
      maskClosable={!saving}
      closable={{ disabled: saving, placement: "end" }}
      className="spr-report-form-drawer"
      footer={
        <div className="spr-report-form-drawer__footer">
          {canEdit ? (
            <Button type="primary" loading={saving} onClick={save}>
              OK
            </Button>
          ) : null}
          <Button onClick={onClose} disabled={saving}>
            Отмена
          </Button>
        </div>
      }
    >
      {error ? <Alert type="error" message={error} showIcon style={{ marginBottom: 12, flexShrink: 0 }} /> : null}
      <div className="spr-report-form-drawer__main">
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            disabled={loading || saving || !canEdit}
            className="spr-report-form-drawer__form"
            colon={false}
          >
            <Form.Item name="idForm" hidden>
              <InputNumber />
            </Form.Item>

            <Row gutter={[8, 6]}>
              <Col xs={24} md={16}>
                <Form.Item name="formName" rules={[{ required: true, message: "Введите название шаблона" }]}>
                  <Input autoFocus placeholder="Название" title="Название шаблона отчета" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} title="Тип отчета (например, заказы)">
                <Form.Item name="formTip" rules={[{ required: true, message: "Выберите тип отчета" }]}>
                  <Select options={REPORT_FORM_TIP_OPTIONS} placeholder="Тип отчета" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[8, 6]}>
              <Col xs={24} sm={12} title="Порядок сортировки в списке">
                <Form.Item name="idx">
                  <InputNumber placeholder="Порядок" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} title="Минимальный уровень пользователя для доступа к отчету">
                <Form.Item name="userLevel">
                  <InputNumber placeholder="Уровень доступа" />
                </Form.Item>
              </Col>
            </Row>

            <div className="spr-report-form-drawer__checks">
              <Form.Item name="isGroup" valuePropName="checked">
                <Checkbox title="Пометить шаблон как группу">Группа</Checkbox>
              </Form.Item>
            </div>

            <Form.Item name="komment">
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 5 }}
                placeholder="Комментарий"
                title="Произвольный комментарий к шаблону"
              />
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Drawer>
  );
};

export default SprReportForm;
